import { useState, useEffect } from 'react';
import { Upload, Plus, Pencil, Trash2, Filter, Eye, EyeOff, Download, Sparkles, Check, X } from 'lucide-react';
import { supabase, Transaction, Category } from '../lib/supabase';
import { parseCSV, type ParsedTransaction } from '../utils/csvParser';
import { parseOFXFile, type OFXTransaction, type OFXParseResult } from '../utils/ofxParser';
import EnhancedImportModal from './EnhancedImportModal';
import { useAuth } from '../contexts/AuthContext';
import { useSmartCategorizationV2 } from '../hooks/useSmartCategorizationV2';
import { useFiscalYear } from '../hooks/useFiscalYear';
import { autoAllocateToBudget } from '../utils/autoAllocation';

export function TransactionsTab() {
  const { effectiveUserId } = useAuth();
  const { startMonth, startDay } = useFiscalYear();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [treasuryAccounts, setTreasuryAccounts] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [showInternalTransfers, setShowInternalTransfers] = useState(true);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState<string>('');
  const [showImport, setShowImport] = useState(false);
  const [showCategoryImport, setShowCategoryImport] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [useBudgetCategories, setUseBudgetCategories] = useState(false);
  const [budgetCategoryIds, setBudgetCategoryIds] = useState<Set<string>>(new Set());
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingTransaction, setEditingTransaction] = useState<string | null>(null);
  const [importError, setImportError] = useState<string>('');
  const [importSuccess, setImportSuccess] = useState<string>('');
  const [newCategory, setNewCategory] = useState({ name: '', type: 'expense' as 'income' | 'expense', is_internal_transfer: false });
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split('T')[0],
    label: '',
    amount: '',
    category_id: '',
    notes: '',
    is_internal_transfer: false
  });
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [showTrainingBanner, setShowTrainingBanner] = useState(false);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importType, setImportType] = useState<'csv' | 'ofx'>('csv');
  const [ofxParseResult, setOfxParseResult] = useState<OFXParseResult | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string>('');

  const smartCategorization = useSmartCategorizationV2();

  useEffect(() => {
    if (effectiveUserId) {
      loadTransactions();
      loadCategories();
      loadBankAccounts();
      loadTreasuryAccounts();
    }
  }, [effectiveUserId]);

  useEffect(() => {
    if (effectiveUserId) {
      loadBudgetCategories();
    }
  }, [effectiveUserId]);

  async function loadTransactions() {
    if (!effectiveUserId) return;

    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', effectiveUserId)
      .order('date', { ascending: false });

    if (!error && data) {
      setTransactions(data);

      const uncategorizedCount = data.filter(t => !t.category_id).length;
      const categorizedCount = data.filter(t => t.category_id).length;

      if (uncategorizedCount > 5 && categorizedCount >= 10 && categories.length > 0 && effectiveUserId) {
        const { data: patterns } = await supabase
          .from('transaction_patterns')
          .select('id')
          .eq('user_id', effectiveUserId)
          .limit(1);

        if (!patterns || patterns.length === 0) {
          setShowTrainingBanner(true);
        }
      }
    }
  }

  useEffect(() => {
    if (transactions.length > 0 && !suggestionsLoaded && categories.length > 0) {
      smartCategorization.loadSuggestionsForTransactions(transactions);
      setSuggestionsLoaded(true);
    }
  }, [transactions, categories, suggestionsLoaded]);

  async function loadBankAccounts() {
    if (!effectiveUserId) return;

    const { data: accounts, error } = await supabase
      .from('bank_accounts')
      .select('*, bank_connections(*)')
      .eq('user_id', effectiveUserId)
      .eq('enabled', true);

    if (!error && accounts) {
      setBankAccounts(accounts);
    }
  }

  async function loadCategories() {
    if (!effectiveUserId) return;

    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('user_id', effectiveUserId)
      .order('name');

    if (!error && data) {
      setCategories(data);
    }
  }

  async function loadTreasuryAccounts() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('treasury_accounts')
      .select('id, name, account_type, is_active')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('name');

    if (!error && data) {
      setTreasuryAccounts(data);
    }
  }

  async function loadBudgetCategories() {
    if (!effectiveUserId) return;

    const { data, error } = await supabase
      .from('budget_lines')
      .select('category_id')
      .eq('user_id', effectiveUserId)
      .not('category_id', 'is', null);

    if (!error && data) {
      const categoryIds = new Set(data.map(line => line.category_id));
      setBudgetCategoryIds(categoryIds);
      console.log('Budget category IDs loaded:', categoryIds);
    } else if (error) {
      console.error('Error loading budget categories:', error);
    }
  }

  // Filter categories based on budget preference
  function getAvailableCategories(): Category[] {
    if (!useBudgetCategories) {
      return categories;
    }
    // Include categories that are either in budget OR are internal transfers
    const filtered = categories.filter(cat =>
      budgetCategoryIds.has(cat.id) || cat.is_internal_transfer
    );
    console.log('Filtering categories:', {
      useBudgetCategories,
      totalCategories: categories.length,
      budgetCategoryIds: Array.from(budgetCategoryIds),
      internalTransferCategories: categories.filter(c => c.is_internal_transfer).length,
      filteredCount: filtered.length
    });
    return filtered;
  }

  async function updateCategoryDetails(categoryId: string, updates: Partial<Category>) {
    try {
      const { error } = await supabase
        .from('categories')
        .update(updates)
        .eq('id', categoryId);

      if (error) {
        console.error('Error updating category:', error);
      } else {
        loadCategories();
        setEditingCategory(null);
      }
    } catch (err) {
      console.error('Error during category update:', err);
    }
  }

  function downloadCSVTemplate() {
    const csvContent = `Date;Libellé;Débits;Crédits
01/09/2024;Cotisation assurance;125,50;
05/09/2024;Subvention municipalité;;1500,00
10/09/2024;Achat fournitures scolaires;87,30;
15/09/2024;Participation parents activité sportive;;250,00
20/09/2024;Facture électricité;156,80;
25/09/2024;Vente kermesse annuelle;;890,45`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'modele-import-transactions.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportError('');
    setImportSuccess('');

    try {
      if (!effectiveUserId) {
        setImportError('Utilisateur non authentifié');
        return;
      }

      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (fileExtension === 'ofx' || fileExtension === 'qfx') {
        console.log('[OFX Import] Processing file:', file.name);

        const result = await parseOFXFile(file);

        if (!result.success || result.transactions.length === 0) {
          setImportError(result.error || 'Aucune transaction trouvée dans le fichier OFX.');
          return;
        }

        console.log(`[OFX Import] ✓ Success: ${result.transactions.length} transactions`);
        if (result.accountId) {
          console.log(`[OFX Import]   Account ID: ${result.accountId}`);
        }
        if (result.startDate && result.endDate) {
          console.log(`[OFX Import]   Period: ${result.startDate} to ${result.endDate}`);
        }
        if (result.ledgerBalance !== undefined) {
          console.log(`[OFX Import]   Ledger Balance: ${result.ledgerBalance}`);
        }

        // Stocker le résultat OFX complet
        setOfxParseResult(result);
        setImportType('ofx');
        setCurrentFileName(file.name);

        // Afficher le modal de confirmation amélioré
        setShowImportConfirm(true);
        setShowImport(false);

        console.log(`[OFX Import] Ready for user confirmation (${result.transactions.length} transactions)`);
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        setImportError('Les fichiers Excel ne sont pas encore supportés. Veuillez exporter votre fichier au format CSV depuis Excel (Fichier > Enregistrer sous > CSV).');
        return;
      } else if (fileExtension === 'csv' || fileExtension === 'txt') {
        const text = await file.text();
        console.log('[CSV Import] Processing file:', file.name);
        console.log('CSV content:', text.substring(0, 200));

        const parsed = parseCSV(text);
        console.log('Parsed transactions:', parsed);

        if (parsed.length === 0) {
          setImportError('Aucune transaction trouvée dans le fichier. Vérifiez que le format CSV contient les colonnes Date, Libellé et Montant.');
          return;
        }

        // Convertir en format OFXParseResult pour compatibilité avec la modale
        const csvAsOfxResult: OFXParseResult = {
          success: true,
          transactions: parsed.map(t => ({
            date: t.date,
            label: t.label,
            amount: t.amount,
            type: t.amount >= 0 ? 'CREDIT' : 'DEBIT'
          }))
        };

        setOfxParseResult(csvAsOfxResult);
        setImportType('csv');
        setCurrentFileName(file.name);

        // Afficher le modal de confirmation
        setShowImportConfirm(true);
        setShowImport(false);

        console.log(`[CSV Import] Ready for user confirmation (${parsed.length} transactions)`);
      } else {
        setImportError('Format de fichier non supporté. Veuillez utiliser un fichier CSV, OFX ou QFX.');
        return;
      }
    } catch (err: any) {
      console.error('Error during import:', err);
      setImportError(`Erreur lors de l'import: ${err.message}`);
    }
  }

  async function insertTransactions(transactions: ParsedTransaction[], fileName: string) {
    try {
      const { error } = await supabase.from('transactions').insert(
        transactions.map(t => ({
          user_id: effectiveUserId,
          date: t.date,
          label: t.label,
          amount: t.amount,
          imported_from: fileName
        }))
      );

      if (error) {
        console.error('Error importing transactions:', error);
        setImportError(`Erreur lors de l'import: ${error.message}`);
      } else {
        setImportSuccess(`${transactions.length} transaction(s) importée(s) avec succès`);
        await loadTransactions();
        setTimeout(() => {
          setShowImport(false);
          setImportSuccess('');
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error inserting transactions:', err);
      setImportError(`Erreur lors de l'import: ${err.message}`);
    }
  }

  async function handleImportConfirm(startBalance?: number, endBalance?: number) {
    if (importType === 'pdf' && startBalance && endBalance) {
      console.log('[PDF Import] Confirmed with balances:', { startBalance, endBalance });
    } else if (importType === 'csv') {
      console.log('[CSV Import] Confirmed');
    }

    await insertTransactions(pendingTransactions, currentFileName);

    // Réinitialiser les états
    setShowImportConfirm(false);
    setPendingTransactions([]);
    setPendingBalances({});
    setCurrentFileName('');
    setParserMethod('');
    setQualityScore(undefined);
    setImportWarnings([]);
  }

  function handleImportCancel() {
    console.log(`[${importType.toUpperCase()} Import] Cancelled by user`);

    // Réinitialiser les états
    setShowImportConfirm(false);
    setPendingTransactions([]);
    setPendingBalances({});
    setCurrentFileName('');
    setParserMethod('');
    setQualityScore(undefined);
    setImportWarnings([]);
    setShowImport(true);
  }

  async function handleCategoryImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (!effectiveUserId) {
        console.error('User not authenticated');
        return;
      }

      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      const categoriesToImport: { user_id: string; name: string; type: 'income' | 'expense' }[] = [];

      for (let i = 1; i < lines.length; i++) {
        const [name, type] = lines[i].split(/[,;]/).map(s => s.trim());
        if (name && (type === 'income' || type === 'expense' || type === 'Recette' || type === 'Dépense')) {
          categoriesToImport.push({
            user_id: effectiveUserId,
            name,
            type: type === 'Recette' ? 'income' : type === 'Dépense' ? 'expense' : type as 'income' | 'expense'
          });
        }
      }

      if (categoriesToImport.length > 0) {
        const { error } = await supabase.from('categories').insert(categoriesToImport);

        if (error) {
          console.error('Error importing categories:', error);
        } else {
          loadCategories();
          setShowCategoryImport(false);
        }
      }
    } catch (err) {
      console.error('Error during category import:', err);
    }
  }

  async function updateCategory(transactionId: string, categoryId: string | null) {
    const transaction = transactions.find(t => t.id === transactionId);

    // Check if the selected category is an internal transfer
    const selectedCategory = categoryId && categoryId !== 'none'
      ? categories.find(c => c.id === categoryId)
      : null;
    const isInternalTransfer = selectedCategory?.is_internal_transfer || false;

    const { error } = await supabase
      .from('transactions')
      .update({
        category_id: categoryId === 'none' ? null : categoryId,
        is_internal_transfer: isInternalTransfer
      })
      .eq('id', transactionId);

    if (!error) {
      if (transaction && categoryId && categoryId !== 'none') {
        await smartCategorization.learnFromManualCategorization(transactionId, categoryId);

        // Auto-allocate to budget
        if (effectiveUserId && startMonth !== null && startDay !== null) {
          await autoAllocateToBudget({
            userId: effectiveUserId,
            transactionId: transaction.id,
            categoryId: categoryId,
            amount: Number(transaction.amount),
            fiscalYearStart: { month: startMonth, day: startDay }
          });
        }
      }
      setSuggestionsLoaded(false);
      loadTransactions();
      setEditingTransaction(null);
    }
  }

  async function deleteTransaction(id: string) {
    const { error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', id);

    if (!error) {
      loadTransactions();
    }
  }

  function toggleTransactionSelection(id: string) {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedTransactions(newSelected);
  }

  function toggleSelectAll() {
    if (selectedTransactions.size === filteredTransactions.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(filteredTransactions.map(t => t.id)));
    }
  }

  async function applyBulkCategory() {
    if (!bulkCategoryId || selectedTransactions.size === 0) return;

    try {
      // Check if the selected category is an internal transfer
      const selectedCategory = bulkCategoryId && bulkCategoryId !== 'none'
        ? categories.find(c => c.id === bulkCategoryId)
        : null;
      const isInternalTransfer = selectedCategory?.is_internal_transfer || false;

      const updates = Array.from(selectedTransactions).map(id => ({
        id,
        category_id: bulkCategoryId === 'none' ? null : bulkCategoryId
      }));

      for (const update of updates) {
        await supabase
          .from('transactions')
          .update({
            category_id: update.category_id,
            is_internal_transfer: isInternalTransfer
          })
          .eq('id', update.id);
      }

      if (bulkCategoryId && bulkCategoryId !== 'none' && effectiveUserId) {
        const transactionsToLearn = transactions.filter(t => selectedTransactions.has(t.id));

        // Learn from each categorized transaction using the V2 system
        for (const transaction of transactionsToLearn) {
          await smartCategorization.learnFromManualCategorization(transaction.id, bulkCategoryId);
        }

        // Auto-allocate all categorized transactions to budget
        if (effectiveUserId && startMonth !== null && startDay !== null) {
          const transactionsToAllocate = transactions.filter(t => selectedTransactions.has(t.id));

          for (const transaction of transactionsToAllocate) {
            await autoAllocateToBudget({
              userId: effectiveUserId,
              transactionId: transaction.id,
              categoryId: bulkCategoryId,
              amount: Number(transaction.amount),
              fiscalYearStart: { month: startMonth, day: startDay }
            });
          }
        }
      }

      setSelectedTransactions(new Set());
      setShowBulkCategoryModal(false);
      setBulkCategoryId('');
      setSuggestionsLoaded(false);
      loadTransactions();
    } catch (err) {
      console.error('Error applying bulk category:', err);
    }
  }

  async function createCategory() {
    if (!newCategory.name.trim()) return;

    try {
      if (!effectiveUserId) {
        console.error('User not authenticated');
        return;
      }

      const { error } = await supabase
        .from('categories')
        .insert([{ ...newCategory, user_id: effectiveUserId }]);

      if (error) {
        console.error('Error creating category:', error);
      } else {
        loadCategories();
        setNewCategory({ name: '', type: 'expense', is_internal_transfer: false });
        setShowCategoryModal(false);
      }
    } catch (err) {
      console.error('Error during category creation:', err);
    }
  }

  async function deleteCategory(categoryId: string) {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ? Les transactions associées perdront leur catégorie.')) {
      return;
    }

    try {
      // First, remove the category from all transactions
      await supabase
        .from('transactions')
        .update({ category_id: null })
        .eq('category_id', categoryId);

      // Then delete the category
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);

      if (error) {
        console.error('Error deleting category:', error);
      } else {
        loadCategories();
        loadTransactions();
      }
    } catch (err) {
      console.error('Error during category deletion:', err);
    }
  }

  async function createTransaction() {
    if (!newTransaction.label.trim() || !newTransaction.amount) {
      setImportError('Veuillez remplir au minimum le libellé et le montant');
      setTimeout(() => setImportError(''), 5000);
      return;
    }

    if (!newTransaction.date) {
      setImportError('Veuillez sélectionner une date');
      setTimeout(() => setImportError(''), 5000);
      return;
    }

    try {
      if (!effectiveUserId) {
        console.error('User not authenticated');
        setImportError('Utilisateur non authentifié');
        setTimeout(() => setImportError(''), 5000);
        return;
      }

      // Check if the selected category is an internal transfer
      const selectedCategory = newTransaction.category_id
        ? categories.find(c => c.id === newTransaction.category_id)
        : null;
      const isInternalTransfer = selectedCategory?.is_internal_transfer || newTransaction.is_internal_transfer;

      const { data, error } = await supabase
        .from('transactions')
        .insert([{
          user_id: effectiveUserId,
          date: newTransaction.date,
          label: newTransaction.label,
          amount: parseFloat(newTransaction.amount),
          category_id: newTransaction.category_id || null,
          notes: newTransaction.notes,
          imported_from: 'Manuel',
          is_internal_transfer: isInternalTransfer
        }])
        .select();

      if (error) {
        console.error('Error creating transaction:', error);
        setImportError(`Erreur lors de la création: ${error.message}`);
        setTimeout(() => setImportError(''), 5000);
        return;
      } else {
        // Auto-allocate to budget if categorized
        if (data && data[0] && newTransaction.category_id && startMonth !== null && startDay !== null) {
          await autoAllocateToBudget({
            userId: effectiveUserId,
            transactionId: data[0].id,
            categoryId: newTransaction.category_id,
            amount: parseFloat(newTransaction.amount),
            fiscalYearStart: { month: startMonth, day: startDay }
          });
        }

        loadTransactions();
        setNewTransaction({
          date: new Date().toISOString().split('T')[0],
          label: '',
          amount: '',
          category_id: '',
          notes: '',
          is_internal_transfer: false
        });
        setShowAddTransactionModal(false);
        setImportSuccess('Transaction créée avec succès');
        setTimeout(() => setImportSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error during transaction creation:', err);
      setImportError(`Erreur inattendue: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
      setTimeout(() => setImportError(''), 5000);
    }
  }

  let filteredTransactions = transactions;

  // Filter by category
  if (selectedCategory !== 'all') {
    filteredTransactions = filteredTransactions.filter(t =>
      t.category_id === selectedCategory || (selectedCategory === 'uncategorized' && !t.category_id)
    );
  }

  // Filter by type (income/expense/internal)
  if (selectedType === 'income') {
    filteredTransactions = filteredTransactions.filter(t => Number(t.amount) > 0 && !t.is_internal_transfer);
  } else if (selectedType === 'expense') {
    filteredTransactions = filteredTransactions.filter(t => Number(t.amount) < 0 && !t.is_internal_transfer);
  } else if (selectedType === 'internal') {
    filteredTransactions = filteredTransactions.filter(t => t.is_internal_transfer);
  }

  // Filter internal transfers visibility
  if (!showInternalTransfers) {
    filteredTransactions = filteredTransactions.filter(t => !t.is_internal_transfer);
  }

  // Filter by treasury account
  if (selectedAccount !== 'all') {
    if (selectedAccount === 'unassigned') {
      filteredTransactions = filteredTransactions.filter(t => !t.treasury_account_id);
    } else {
      filteredTransactions = filteredTransactions.filter(t => t.treasury_account_id === selectedAccount);
    }
  }

  const balance = transactions
    .filter(t => !t.is_internal_transfer)
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const totalBankBalance = bankAccounts.reduce((sum, acc) => sum + Number(acc.balance), 0);

  const categorizedCount = transactions.filter(t => t.category_id).length;
  const uncategorizedCount = transactions.filter(t => !t.category_id).length;

  return (
    <div className="space-y-6">
      {showTrainingBanner && !smartCategorization.isLearning && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <Sparkles size={24} className="text-purple-600 mt-1" />
              <div>
                <h3 className="font-semibold text-purple-900 mb-1">
                  <Sparkles size={16} className="inline mr-1" />
                  Classification automatique intelligente
                </h3>
                <p className="text-sm text-purple-700 mb-3">
                  J'ai détecté que vous avez catégorisé {categorizedCount} transactions. Je peux apprendre de vos choix pour suggérer automatiquement des catégories aux {uncategorizedCount} transactions non catégorisées et aux nouvelles.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await smartCategorization.trainInitialModel(transactions);
                        await smartCategorization.loadSuggestionsForTransactions(transactions);
                        setShowTrainingBanner(false);
                        setImportSuccess('Classification automatique activée ! Les suggestions sont disponibles dans l\'onglet Catégorisation.');
                        setTimeout(() => setImportSuccess(''), 5000);
                      } catch (error) {
                        console.error('Error training model:', error);
                        setImportError(error instanceof Error ? error.message : 'Erreur lors de l\'activation de la classification automatique');
                        setTimeout(() => setImportError(''), 5000);
                      }
                    }}
                    disabled={smartCategorization.isLearning}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {smartCategorization.isLearning ? 'Entraînement en cours...' : 'Activer la classification automatique'}
                  </button>
                  <button
                    onClick={() => setShowTrainingBanner(false)}
                    className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors text-sm font-medium"
                  >
                    Plus tard
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowTrainingBanner(false)}
              className="text-purple-400 hover:text-purple-600"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {importError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <X size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{importError}</p>
            </div>
            <button
              onClick={() => setImportError('')}
              className="text-red-400 hover:text-red-600"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {importSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Check size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-green-800">{importSuccess}</p>
            </div>
            <button
              onClick={() => setImportSuccess('')}
              className="text-green-400 hover:text-green-600"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {categorizedCount < 10 && uncategorizedCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Sparkles size={20} className="text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 text-sm mb-1">
                Astuce : Classification automatique
              </h4>
              <p className="text-sm text-blue-700">
                Catégorisez au moins 10 transactions manuellement, puis je pourrai apprendre de vos choix et suggérer automatiquement des catégories pour les suivantes. Actuellement : {categorizedCount}/10
              </p>
            </div>
          </div>
        </div>
      )}

      {smartCategorization.isLearning && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="text-sm text-blue-800">Apprentissage en cours...</span>
          </div>
        </div>
      )}

      {bankAccounts.length > 0 && (
        <div className="space-y-4">
          <button
            onClick={() => setShowBankDetails(!showBankDetails)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title={showBankDetails ? "Masquer les comptes bancaires" : "Afficher les comptes bancaires"}
          >
            {showBankDetails ? <EyeOff size={18} /> : <Eye size={18} />}
            <span className="text-sm font-medium">
              {showBankDetails ? "Masquer les comptes bancaires" : "Afficher les comptes bancaires"}
            </span>
          </button>

          {showBankDetails && (
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Comptes bancaires connectés</h3>
              <p className="text-sm text-gray-600 mb-4 italic">Fonctionnalité en cours de développement</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bankAccounts.map((account) => (
                  <div key={account.id} className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {account.account_name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {account.bank_connections?.bank_name || 'Banque'}
                          {account.account_number && ` • •••• ${account.account_number}`}
                        </p>
                      </div>
                    </div>
                    <p className="text-2xl font-bold mt-2" style={{ color: account.balance >= 0 ? '#059669' : '#dc2626' }}>
                      {Number(account.balance).toLocaleString('fr-FR', { style: 'currency', currency: account.currency })}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Solde total des comptes bancaires:</span>
                  <span className="text-2xl font-bold" style={{ color: totalBankBalance >= 0 ? '#059669' : '#dc2626' }}>
                    {totalBankBalance.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Trésorerie</h2>
          <p className="text-3xl font-bold mt-2" style={{ color: balance >= 0 ? '#059669' : '#dc2626' }}>
            {balance.toFixed(2)} €
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCategoryManager(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Filter size={20} />
            Catégories
          </button>
          <button
            onClick={() => setShowCategoryImport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <Upload size={20} />
            Importer catégories
          </button>
          <button
            onClick={() => setShowAddTransactionModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus size={20} />
            Opération
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Upload size={20} />
            Importer opérations
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Filter size={20} className="text-gray-500" />

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Toutes les catégories</option>
          <option value="uncategorized">Non catégorisées</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>

        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tous les types</option>
          <option value="income">Recettes uniquement</option>
          <option value="expense">Dépenses uniquement</option>
          <option value="internal">Virements internes</option>
        </select>

        <select
          value={selectedAccount}
          onChange={(e) => setSelectedAccount(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tous les comptes</option>
          <option value="unassigned">Non assignées</option>
          {treasuryAccounts.map(account => (
            <option key={account.id} value={account.id}>{account.name}</option>
          ))}
        </select>

        <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="checkbox"
            checked={showInternalTransfers}
            onChange={(e) => setShowInternalTransfers(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <span className="text-sm text-gray-700">Afficher virements internes</span>
        </label>

        {selectedTransactions.size > 0 && (
          <button
            onClick={() => setShowBulkCategoryModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Pencil size={16} />
            Catégoriser ({selectedTransactions.size})
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedTransactions.size === filteredTransactions.length && filteredTransactions.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-blue-600 rounded"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Libellé</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredTransactions.map(transaction => (
              <tr key={transaction.id} className={`hover:bg-gray-50 ${selectedTransactions.has(transaction.id) ? 'bg-blue-50' : ''}`}>
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedTransactions.has(transaction.id)}
                    onChange={() => toggleTransactionSelection(transaction.id)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {new Date(transaction.date).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900">{transaction.label}</span>
                    {transaction.imported_from && transaction.imported_from !== 'Manuel' && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                        {transaction.imported_from}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {editingTransaction === transaction.id ? (
                    <select
                      autoFocus
                      value={transaction.category_id || 'none'}
                      onChange={(e) => updateCategory(transaction.id, e.target.value)}
                      onBlur={() => setEditingTransaction(null)}
                      className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">Non catégorisée</option>
                      {getAvailableCategories().map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center gap-2">
                      {!transaction.category_id && smartCategorization.suggestions.has(transaction.id) ? (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                            <Sparkles size={14} className="text-purple-600" />
                            <span className="text-xs font-medium text-purple-700">
                              {smartCategorization.suggestions.get(transaction.id)?.category_name}
                            </span>
                            <span className="text-xs text-purple-500">
                              ({Math.round((smartCategorization.suggestions.get(transaction.id)?.confidence_score || 0) * 100)}%)
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              const suggestion = smartCategorization.suggestions.get(transaction.id);
                              if (suggestion) {
                                smartCategorization.acceptSuggestion(
                                  transaction.id,
                                  suggestion.category_id,
                                  transaction.label
                                ).then(() => {
                                  setSuggestionsLoaded(false);
                                  loadTransactions();
                                });
                              }
                            }}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Accepter la suggestion"
                            disabled={smartCategorization.isLearning}
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => {
                              smartCategorization.rejectSuggestion(transaction.id);
                            }}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Refuser la suggestion"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-600">
                          {categories.find(c => c.id === transaction.category_id)?.name || 'Non catégorisée'}
                        </span>
                      )}
                      <button
                        onClick={() => setEditingTransaction(transaction.id)}
                        className="text-gray-400 hover:text-blue-600"
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  <div className="flex items-center justify-end gap-2">
                    {(transaction.is_internal_transfer || categories.find(c => c.id === transaction.category_id)?.is_internal_transfer) && (
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">Virement interne</span>
                    )}
                    <span className="font-medium" style={{ color: Number(transaction.amount) >= 0 ? '#059669' : '#dc2626' }}>
                      {Number(transaction.amount).toFixed(2)} €
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => deleteTransaction(transaction.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Importer des transactions</h3>
            <div className="mb-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Format CSV :</p>
                <p className="text-sm text-gray-600">
                  Le fichier CSV doit contenir les colonnes suivantes :
                </p>
                <ul className="text-sm text-gray-600 list-disc list-inside ml-2">
                  <li><strong>Date, Libellé, Débits, Crédits</strong> (format recommandé)</li>
                  <li><em>ou</em> Date, Libellé, Montant (format simple)</li>
                </ul>
                <button
                  onClick={downloadCSVTemplate}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium mt-2"
                >
                  <Download size={16} />
                  Télécharger le modèle CSV
                </button>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <p className="text-sm font-semibold text-gray-700 mb-2">Format OFX/QFX :</p>
                <p className="text-sm text-gray-600 mb-2">
                  Format standard bancaire pour l'import automatique de vos transactions.
                </p>
                <p className="text-sm text-gray-600 font-medium mb-1">Comment obtenir ce fichier :</p>
                <ul className="text-sm text-gray-600 list-disc list-inside ml-2 space-y-1">
                  <li>Connectez-vous à votre espace bancaire en ligne</li>
                  <li>Accédez à vos relevés de compte</li>
                  <li>Cherchez "Exporter" ou "Télécharger"</li>
                  <li>Sélectionnez le format <strong>OFX</strong>, <strong>QFX</strong> ou <strong>Quicken</strong></li>
                </ul>
                <p className="text-xs text-gray-500 mt-2 italic">
                  Si vous ne trouvez pas cette option, contactez votre banque ou utilisez le format CSV.
                </p>
              </div>
            </div>

            {importError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {importError}
              </div>
            )}

            {importSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                {importSuccess}
              </div>
            )}

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Sélectionnez votre fichier
              </label>
              <input
                type="file"
                accept=".csv,.ofx,.qfx"
                onChange={handleFileUpload}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500">
                Formats acceptés : CSV, OFX, QFX
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportError('');
                  setImportSuccess('');
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {showCategoryImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Importer des catégories CSV</h3>
            <p className="text-sm text-gray-600 mb-4">
              Le fichier CSV doit contenir les colonnes : Nom, Type
              <br />
              <span className="text-xs">Type peut être : income, expense, Recette ou Dépense</span>
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleCategoryImport}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCategoryImport(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {showCategoryManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold">Gestion des catégories</h3>
              <button
                onClick={() => setShowCategoryManager(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Option pour utiliser strictement les catégories du budget */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="useBudgetCategories"
                  checked={useBudgetCategories}
                  onChange={(e) => setUseBudgetCategories(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 rounded"
                />
                <div className="flex-1">
                  <label htmlFor="useBudgetCategories" className="block font-medium text-gray-900 cursor-pointer">
                    Utiliser strictement les catégories du budget prévisionnel
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    Lorsque cette option est activée, seules les catégories présentes dans le budget prévisionnel seront disponibles pour les transactions. Les catégories de virements internes restent toujours disponibles.
                  </p>
                  {useBudgetCategories && (
                    <div className="mt-2 p-2 bg-white border border-blue-300 rounded">
                      <p className="text-sm font-medium text-gray-900">
                        {budgetCategoryIds.size === 0 ? (
                          <span className="text-orange-600">
                            Aucune catégorie du budget trouvée. Seules les catégories de virements internes seront disponibles. Créez des lignes dans votre budget prévisionnel (onglet Budget) pour débloquer plus de catégories.
                          </span>
                        ) : (
                          <span className="text-green-600">
                            {budgetCategoryIds.size} catégorie{budgetCategoryIds.size > 1 ? 's' : ''} du budget disponible{budgetCategoryIds.size > 1 ? 's' : ''} pour la catégorisation
                            {categories.filter(c => c.is_internal_transfer).length > 0 && ` + ${categories.filter(c => c.is_internal_transfer).length} virement${categories.filter(c => c.is_internal_transfer).length > 1 ? 's' : ''} interne${categories.filter(c => c.is_internal_transfer).length > 1 ? 's' : ''}.`}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bouton pour ajouter une nouvelle catégorie */}
            <button
              onClick={() => setShowCategoryModal(true)}
              className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={20} />
              Ajouter une catégorie
            </button>

            {/* Liste des catégories */}
            <div className="space-y-4">
              <h4 className="font-semibold text-lg text-gray-800">Catégories de recettes</h4>
              <div className="space-y-2">
                {categories.filter(c => c.type === 'income').length === 0 ? (
                  <p className="text-gray-500 text-sm italic">Aucune catégorie de recette</p>
                ) : (
                  categories.filter(c => c.type === 'income').map(category => (
                    <div key={category.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="font-medium text-gray-900">{category.name}</div>
                        {category.is_internal_transfer && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Virement interne</span>
                        )}
                        {budgetCategoryIds.has(category.id) && (
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Budget</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingCategory(category)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Modifier cette catégorie"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => deleteCategory(category.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Supprimer cette catégorie"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <h4 className="font-semibold text-lg text-gray-800 mt-6">Catégories de dépenses</h4>
              <div className="space-y-2">
                {categories.filter(c => c.type === 'expense').length === 0 ? (
                  <p className="text-gray-500 text-sm italic">Aucune catégorie de dépense</p>
                ) : (
                  categories.filter(c => c.type === 'expense').map(category => (
                    <div key={category.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="font-medium text-gray-900">{category.name}</div>
                        {category.is_internal_transfer && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Virement interne</span>
                        )}
                        {budgetCategoryIds.has(category.id) && (
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Budget</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingCategory(category)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Modifier cette catégorie"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => deleteCategory(category.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Supprimer cette catégorie"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCategoryManager(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Modifier la catégorie</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Subventions, Salaires..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={editingCategory.type}
                  onChange={(e) => setEditingCategory({ ...editingCategory, type: e.target.value as 'income' | 'expense' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="income">Recette</option>
                  <option value="expense">Dépense</option>
                </select>
              </div>
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="edit_category_is_internal_transfer"
                  checked={editingCategory.is_internal_transfer}
                  onChange={(e) => setEditingCategory({ ...editingCategory, is_internal_transfer: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="edit_category_is_internal_transfer" className="text-sm text-gray-700">
                  Catégorie de virement interne
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingCategory(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => updateCategoryDetails(editingCategory.id, {
                  name: editingCategory.name,
                  type: editingCategory.type,
                  is_internal_transfer: editingCategory.is_internal_transfer
                })}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Nouvelle catégorie</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Subventions, Salaires..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newCategory.type}
                  onChange={(e) => setNewCategory({ ...newCategory, type: e.target.value as 'income' | 'expense' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="income">Recette</option>
                  <option value="expense">Dépense</option>
                </select>
              </div>
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="category_is_internal_transfer"
                  checked={newCategory.is_internal_transfer}
                  onChange={(e) => setNewCategory({ ...newCategory, is_internal_transfer: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="category_is_internal_transfer" className="text-sm text-gray-700">
                  Catégorie de virement interne
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setNewCategory({ name: '', type: 'expense', is_internal_transfer: false });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createCategory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddTransactionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Ajouter une opération</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={newTransaction.date}
                  onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Libellé</label>
                <input
                  type="text"
                  value={newTransaction.label}
                  onChange={(e) => setNewTransaction({ ...newTransaction, label: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Paiement adhésions..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant (€) - positif pour recette, négatif pour dépense
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newTransaction.amount}
                  onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                <select
                  value={newTransaction.category_id}
                  onChange={(e) => setNewTransaction({ ...newTransaction, category_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Non catégorisée</option>
                  {getAvailableCategories().map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} ({cat.type === 'income' ? 'Recette' : 'Dépense'})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
                <textarea
                  value={newTransaction.notes}
                  onChange={(e) => setNewTransaction({ ...newTransaction, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Notes additionnelles..."
                />
              </div>
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="is_internal_transfer"
                  checked={newTransaction.is_internal_transfer}
                  onChange={(e) => setNewTransaction({ ...newTransaction, is_internal_transfer: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="is_internal_transfer" className="text-sm text-gray-700">
                  Virement interne (n'affecte pas le solde)
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddTransactionModal(false);
                  setNewTransaction({
                    date: new Date().toISOString().split('T')[0],
                    label: '',
                    amount: '',
                    category_id: '',
                    notes: '',
                    is_internal_transfer: false
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createTransaction}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Catégoriser en masse</h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedTransactions.size} transaction(s) sélectionnée(s)
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select
                value={bulkCategoryId}
                onChange={(e) => setBulkCategoryId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sélectionner une catégorie</option>
                <option value="none">Non catégorisée</option>
                {getAvailableCategories().map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} ({cat.type === 'income' ? 'Recette' : 'Dépense'})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowBulkCategoryModal(false);
                  setBulkCategoryId('');
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={applyBulkCategory}
                disabled={!bulkCategoryId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportConfirm && ofxParseResult && (
        <EnhancedImportModal
          parseResult={ofxParseResult}
          importedFileName={currentFileName}
          onClose={() => {
            setShowImportConfirm(false);
            setOfxParseResult(null);
          }}
          onConfirm={() => {
            setShowImportConfirm(false);
            setOfxParseResult(null);
            loadTransactions();
            setImportSuccess(`Import réussi`);
            setTimeout(() => setImportSuccess(''), 3000);
          }}
          allowAccountSelection={true}
          allowBalanceUpdate={false}
        />
      )}
    </div>
  );
}
